import React, { useMemo, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import { DataGrid, gridClasses, GridColDef } from "@mui/x-data-grid";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { CompanyLogo } from "../../../../../../components/CompanyLogo";
import { LightTooltip } from "../../../../../../components/LightTooltip";
import { formatNumber } from "../../../../../../utils/numberUtils";
import {
  useInstallsTable,
  TableInstallRow,
  DataInstallRow,
} from "../hooks/useInstallsTable";
import { lightScrollbarStyle } from "../../../../../../styles/lightScrollbarStyle";
import { EmptyState } from "../../../../components/EmptyState";
import { InstallsLocationsDrawer } from "./InstallsLocationsDrawer";
import { HgProduct } from "../../../../../../types/taxonomy";
import Settings from "@mui/icons-material/Settings";

interface InstallsTableProps {
  hgId: string;
  searchTerm: string;
}

type SelectedProductOption = Pick<HgProduct, "id" | "name">;

const formatDate = (date?: Date | string) => {
  if (!date) return "";

  const dateObj = typeof date === "string" ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return "";

  return dateObj.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// Make the first occurrence of product name bold in description
const formatDescriptionWithBoldName = (description: string, name: string) => {
  if (!name || !description) return description;

  const firstMatch = description.toLowerCase().indexOf(name.toLowerCase());
  if (firstMatch === -1) return description;

  const beforeMatch = description.substring(0, firstMatch);
  const match = description.substring(firstMatch, firstMatch + name.length);
  const afterMatch = description.substring(firstMatch + name.length);

  return `${beforeMatch}<strong>${match}</strong>${afterMatch}`;
};

const HeaderWithTooltip: React.FC<{
  title: string;
  tooltip?: string;
}> = React.memo(({ title, tooltip }) => {
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="subtitle2" fontWeight="bold">
        {title}
      </Typography>
      {tooltip && (
        <LightTooltip
          title={
            <Typography
              variant="body2"
              dangerouslySetInnerHTML={{
                __html: tooltip.replace(
                  /\*\*(.*?)\*\*/g,
                  "<strong>$1</strong>"
                ),
              }}
            />
          }
        >
          <IconButton size="small" sx={{ p: 0 }}>
            <HelpOutlineIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          </IconButton>
        </LightTooltip>
      )}
    </Stack>
  );
});

HeaderWithTooltip.displayName = "HeaderWithTooltip";

export const InstallsTable: React.FC<InstallsTableProps> = React.memo(
  ({ hgId, searchTerm }) => {
    const {
      sortModel,
      setSortModel,
      paginationModel,
      setPaginationModel,
      loading,
      rows,
      resultsCount,
      hasResults,
    } = useInstallsTable({
      hgId,
      searchTerm,
    });

    const [locationsDrawer, setLocationsDrawer] = useState<{
      open: boolean;
      product: SelectedProductOption | null;
      productLogo: string;
    }>({
      open: false,
      product: null,
      productLogo: "",
    });

    const handleLocationsClick = useCallback(
      (product: SelectedProductOption, productLogo: string) => {
        setLocationsDrawer({
          open: true,
          product,
          productLogo,
        });
      },
      []
    );

    const handleDrawerClose = useCallback(() => {
      setLocationsDrawer({
        open: false,
        product: null,
        productLogo: "",
      });
    }, []);

    const showEmptyState = !loading && !hasResults;

    const columnsDefinition: GridColDef<TableInstallRow>[] = useMemo(
      () => [
        {
          field: "productName",
          headerName: "Product",
          minWidth: 200,
          sortable: true,
          flex: 1,
          renderHeader: () => <HeaderWithTooltip title="Product" />,
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return (
                <Stack direction="row" alignItems="center" gap={1} flex={1}>
                  <Skeleton variant="rounded" width={24} height={24} />
                  <Skeleton variant="text" sx={{ flexGrow: 1 }} />
                </Stack>
              );
            }
            const rowData = params.row as DataInstallRow;
            const vendorUrl = rowData.product?.vendor?.url || "";
            const productName = rowData.product?.name || "";
            const productDescription =
              rowData.product?.description || productName;

            // Remove "(Unspecified Product)" from productName for formatting
            const cleanProductName = productName
              .replace(/\s*\(Unspecified Product\)\s*/gi, "")
              .trim();

            const formattedDescription = formatDescriptionWithBoldName(
              productDescription,
              cleanProductName
            );

            return (
              <LightTooltip
                title={
                  <Typography
                    variant="body2"
                    dangerouslySetInnerHTML={{
                      __html: formattedDescription,
                    }}
                  />
                }
                placement="right"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  overflow="hidden"
                >
                  <CompanyLogo
                    domain={vendorUrl}
                    size={24}
                    variant="rounded"
                    fallback={<Settings />}
                  />
                  <Typography variant="body2" noWrap>
                    {productName}
                  </Typography>
                </Stack>
              </LightTooltip>
            );
          },
        },
        {
          field: "vendorName",
          headerName: "Vendor",
          minWidth: 150,
          flex: 1,
          sortable: true,
          renderHeader: () => <HeaderWithTooltip title="Vendor" />,
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="80%" />;
            }
            const rowData = params.row as DataInstallRow;
            const vendorName = rowData.product?.vendor?.name || "";

            return (
              <Tooltip title={vendorName} placement="bottom-start">
                <Typography variant="body2" noWrap>
                  {vendorName}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          field: "productCategory",
          headerName: "Product Category",
          minWidth: 180,
          flex: 1,
          sortable: true,
          renderHeader: () => <HeaderWithTooltip title="Product Category" />,
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="70%" />;
            }
            const rowData = params.row as DataInstallRow;
            const categoryName = rowData.product?.category?.name || "";
            const categoryTreeName = (
              rowData.product?.category?.nameTree || []
            ).join(" > ");

            return (
              <Tooltip title={categoryTreeName} placement="bottom-start">
                <Typography variant="body2" noWrap>
                  {categoryName}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          field: "productAttributes",
          headerName: "Product Attributes",
          minWidth: 220,
          sortable: true,
          renderHeader: () => <HeaderWithTooltip title="Product Attributes" />,
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="90%" />;
            }
            const rowData = params.row as DataInstallRow;
            const attributes = rowData.product?.attributeNames || [];
            const attributesText = attributes.join(", ");

            return (
              <Tooltip title={attributesText} placement="bottom-start">
                <Typography variant="body2" noWrap>
                  {attributesText}
                </Typography>
              </Tooltip>
            );
          },
        },
        {
          field: "locations",
          headerName: "Locations",
          width: 100,
          sortable: true,
          renderHeader: () => <HeaderWithTooltip title="Locations" />,
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return (
                <Skeleton
                  variant="rounded"
                  width={50}
                  height={22}
                  sx={{ borderRadius: "16px" }}
                />
              );
            }
            const rowData = params.row as DataInstallRow;
            return (
              <Link
                component="button"
                variant="body2"
                onClick={() =>
                  handleLocationsClick(
                    {
                      id: rowData.product?.id || 0,
                      name: rowData.product?.name || "",
                    },
                    rowData.product?.vendor?.url || ""
                  )
                }
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  "&:hover": {
                    textDecoration: "none",
                  },
                }}
              >
                {formatNumber(rowData.locations || 0)}
              </Link>
            );
          },
        },
        {
          field: "firstVerified",
          headerName: "First Verified",
          width: 130,
          sortable: true,
          renderHeader: () => (
            <HeaderWithTooltip
              title="First Verified"
              tooltip="**First Verified Date** is the first date in which we detected a signal for this product at this company."
            />
          ),
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="80%" />;
            }
            const rowData = params.row as DataInstallRow;
            return (
              <Typography variant="body2">
                {formatDate(rowData.firstVerified)}
              </Typography>
            );
          },
        },
        {
          field: "lastVerified",
          headerName: "Last Verified",
          width: 130,
          sortable: true,
          renderHeader: () => (
            <HeaderWithTooltip
              title="Last Verified"
              tooltip="**Last Verified Date** is the most recent date in which we have detected a signal for this product at this company."
            />
          ),
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="80%" />;
            }
            const rowData = params.row as DataInstallRow;
            return (
              <Typography variant="body2">
                {formatDate(rowData.lastVerified)}
              </Typography>
            );
          },
        },
        {
          field: "intensity",
          headerName: "Intensity",
          width: 110,
          sortable: true,
          renderHeader: () => (
            <HeaderWithTooltip
              title="Intensity"
              tooltip="**Intensity** is a count of unique install signals detected for this product. When multiple products in a competitive group are detected, intensity is weighted to better distinguish the most relevant product(s)."
            />
          ),
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="60%" />;
            }
            const rowData = params.row as DataInstallRow;
            return (
              <Typography variant="body2">
                {formatNumber(rowData.intensity || 0)}
              </Typography>
            );
          },
        },
        {
          field: "url",
          headerName: "URL",
          sortable: true,
          minWidth: 100,
          renderHeader: () => <HeaderWithTooltip title="URL" />,
          renderCell: (params) => {
            if (params.row.isSkeleton) {
              return <Skeleton variant="text" width="80%" />;
            }
            const rowData = params.row as DataInstallRow;
            return (
              <Tooltip title={rowData.url} placement="bottom-start">
                <Typography variant="body2" noWrap>
                  {rowData.url}
                </Typography>
              </Tooltip>
            );
          },
        },
      ],
      [handleLocationsClick]
    );

    return (
      <Box>
        {loading ? (
          <Stack direction="row" alignItems="center" height={24} mb={2}>
            <Skeleton
              variant="text"
              width={24}
              height={40}
              sx={{ display: "inline-block", mr: 1 }}
            />
            <Typography variant="h5">Product installs found</Typography>
          </Stack>
        ) : (
          <Typography variant="h5" height={24} mb={2}>
            {`${formatNumber(resultsCount || 0)} Product installs found`}
          </Typography>
        )}

        {showEmptyState ? (
          <EmptyState
            title="No product installs found"
            description={
              searchTerm.trim()
                ? `No product installs match "${searchTerm}"`
                : "No product install data found for this company at this time"
            }
            height={650}
          />
        ) : (
          <Box sx={{ height: 650, width: "100%", marginLeft: -1 }}>
            <DataGrid
              rows={rows}
              columns={columnsDefinition}
              pageSizeOptions={[25, 50, 100]}
              paginationMode="server"
              rowCount={resultsCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              getRowId={(row) => {
                if (row.isSkeleton) {
                  return `skeleton-${Math.random()}`;
                }
                const dataRow = row as DataInstallRow;
                const productId = dataRow.product?.id;
                const vendorId = dataRow.product?.vendor?.id;
                const { url, firstVerified } = dataRow;

                return `${productId}-${vendorId}-${url}-${firstVerified}`;
              }}
              disableColumnFilter
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnResize
              disableColumnSelector
              slotProps={{
                pagination: {
                  showFirstButton: true,
                  showLastButton: true,
                },
              }}
              sx={{
                [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: "none",
                  },
                [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
                  {
                    outline: "none",
                  },
                [`& .${gridClasses.columnSeparator}`]: {
                  [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
                    display: "none",
                  },
                },
                "& .MuiDataGrid-columnHeader": {
                  borderBottom: "none !important",
                },
                "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                  {
                    "& .MuiIconButton-root": {
                      padding: 0,
                    },
                    "& .MuiTouchRipple-root": {
                      display: "none",
                    },
                  },
                "&, [class^=MuiDataGrid]": { border: "none" },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-filler": {
                  display: "none",
                },
                ".MuiDataGrid-scrollbarFiller": {
                  display: "none",
                },
                "& .MuiDataGrid-scrollbar--vertical": {
                  ...lightScrollbarStyle,
                },
                "& .MuiDataGrid-scrollbar--horizontal": {
                  ...lightScrollbarStyle,
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                },
              }}
            />
          </Box>
        )}

        {locationsDrawer.open && (
          <InstallsLocationsDrawer
            open={locationsDrawer.open}
            onClose={handleDrawerClose}
            companyId={hgId}
            product={locationsDrawer.product}
            productLogo={locationsDrawer.productLogo}
          />
        )}
      </Box>
    );
  }
);

InstallsTable.displayName = "InstallsTable";
